import { Cashflow } from '../types/cashflow';

/**
 * 지표 — **저장하지 않고 매번 계산한다.**
 *
 * 저장하면 원장이 한 줄 늘 때마다 과거 값이 조용히 틀린 채로 남는다.
 * 설계: `docs/02-design/000-data-model.md` 5절.
 *
 * ⚠️ **손계산과 대조해 검증한다.** 투자 금액은 틀리면 안 되는 데이터다
 *    (`docs/STATUS.md` 「알아둘 것」).
 */
export interface Metrics {
  /** 투자한 원금 */
  invested: number;
  /** 회수한 돈 (회수 + 배당) */
  returned: number;
  /** 아직 안 판 것의 평가액 */
  residual: number;
  /** 회수금 ÷ 투자원금 — 실제로 손에 쥔 배수 */
  dpi: number | null;
  /** 아직 안 판 것의 배수 */
  rvpi: number | null;
  /** DPI + RVPI — 지금까지의 총 성적 */
  tvpi: number | null;
  /** 연 수익률. 계산이 안 되면 null */
  irr: number | null;
  /** 참여자가 실제로 넣은 돈 */
  contributed: number;
  /** 참여자에게 돌려준 돈 */
  distributed: number;
  /** GRK 가 가져간 보수 — 회사 매출 */
  fee: number;
}

const sum = (rows: Cashflow[]) => rows.reduce((s, r) => s + r.amount, 0);

/**
 * @param asOf 남은 평가액을 얹는 날. 기본은 오늘 — 지난 기간 보고서는 기간 말일을 넘긴다
 *   (안 그러면 2025 온기 보고를 2026 에 뽑을 때 IRR 이 오늘까지 늘어난 기간으로 낮아진다).
 */
export function computeMetrics(flows: Cashflow[], residual: number, asOf: Date = new Date()): Metrics {
  const of = (kind: string) => flows.filter((f) => f.kind === kind);

  const invested = sum(of('investment'));
  const returned = sum(of('exit')) + sum(of('dividend'));
  const contributed = sum(of('contribution'));
  const distributed = sum(of('distribution'));
  const fee = sum(of('fee'));

  const ratio = (v: number) => (invested > 0 ? v / invested : null);

  return {
    invested,
    returned,
    residual,
    dpi: ratio(returned),
    rvpi: ratio(residual),
    tvpi: ratio(returned + residual),
    irr: xirr(toSeries(flows, residual, asOf)),
    contributed,
    distributed,
    fee,
  };
}

/**
 * 투자 관점 현금흐름 — 나간 투자금은 음수, 들어온 회수는 양수.
 * 아직 안 판 것은 **기준일(기본 오늘)에 판 셈 치고** 마지막에 얹는다. 안 그러면 진행 중인 건의
 * 수익률이 -100% 로 나온다.
 */
function toSeries(flows: Cashflow[], residual: number, asOf: Date): Array<[Date, number]> {
  const out: Array<[Date, number]> = [];
  flows.forEach((f) => {
    const d = new Date(f.occurredOn);
    if (isNaN(d.getTime())) return;
    if (f.kind === 'investment') out.push([d, -f.amount]);
    if (f.kind === 'exit' || f.kind === 'dividend') out.push([d, f.amount]);
  });
  if (out.length === 0) return out;
  if (residual > 0) out.push([asOf, residual]);
  return out.sort((a, b) => a[0].getTime() - b[0].getTime());
}

const DAY = 86_400_000;

/**
 * XIRR — 날짜가 고르지 않은 현금흐름의 연 수익률.
 *
 * 뉴턴법으로 푼다. 부호가 한 번도 안 바뀌면(전부 지출이거나 전부 수입) 해가 없다.
 */
export function xirr(series: Array<[Date, number]>): number | null {
  if (series.length < 2) return null;
  const hasNeg = series.some(([, v]) => v < 0);
  const hasPos = series.some(([, v]) => v > 0);
  if (!hasNeg || !hasPos) return null;

  const t0 = series[0][0].getTime();
  const years = series.map(([d]) => (d.getTime() - t0) / DAY / 365);
  const vals = series.map(([, v]) => v);

  const npv = (r: number) => vals.reduce((s, v, i) => s + v / Math.pow(1 + r, years[i]), 0);
  const dnpv = (r: number) =>
    vals.reduce((s, v, i) => s - (years[i] * v) / Math.pow(1 + r, years[i] + 1), 0);

  let r = 0.1;
  for (let i = 0; i < 80; i++) {
    const f = npv(r);
    if (!isFinite(f)) return null;
    if (Math.abs(f) < 1e-6) return r;
    const d = dnpv(r);
    if (!isFinite(d) || Math.abs(d) < 1e-12) break;
    const next = r - f / d;
    // -100% 아래로는 의미가 없다
    if (next <= -0.9999) { r = -0.9999; break; }
    if (Math.abs(next - r) < 1e-9) { r = next; break; }
    r = next;
  }
  const check = npv(r);
  if (!isFinite(r) || !isFinite(check) || Math.abs(check) > 1) return bisect(npv);
  return r;
}

/** 뉴턴법이 벗어나면 구간을 좁혀 찾는다 */
function bisect(npv: (r: number) => number): number | null {
  let lo = -0.9999;
  let hi = 10;
  let flo = npv(lo);
  let fhi = npv(hi);
  if (!isFinite(flo) || !isFinite(fhi) || flo * fhi > 0) return null;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const fm = npv(mid);
    if (Math.abs(fm) < 1e-7) return mid;
    if (flo * fm < 0) { hi = mid; fhi = fm; } else { lo = mid; flo = fm; }
  }
  return (lo + hi) / 2;
}

/** 반올림해서 0 이 되는 아주 작은 음수가 「-0.0」으로 찍히지 않게 — 부호를 뗀다 (#32) */
const fixed = (v: number, digits: number) => {
  const s = v.toFixed(digits);
  return Number(s) === 0 ? s.replace('-', '') : s;
};

export const pct = (v: number | null): string =>
  v === null ? '—' : `${fixed(v * 100, 1)}%`;

export const mult = (v: number | null): string =>
  v === null ? '—' : `${fixed(v, 2)}x`;
